import { Alert, AlertTitle } from '@mui/material';
import { RiShip2Line } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import { JourneyInfo } from '../../../../types/journey';
import { DAYJS_VISUALIZATION_DATE_FORMAT } from '../../../../utils/date';
import { VesselCertificateStatus } from '../../../../types/core';

type VesselCertificatesAlertProps = {
  selectedVesselId: string;
  journey: JourneyInfo;
  vesselName: string;
};


const VesselCertificatesAlert = ({
  selectedVesselId,
  journey,
  vesselName,
}: VesselCertificatesAlertProps) => {
  const { getVesselCertificates } = useIotsApi();
  const { t } = useLanguage();

  const { data: vesselCertificates = [] } = useQuery({
    queryKey: ['vessel', selectedVesselId ?? '', 'certificates'],
    queryFn: () => getVesselCertificates(selectedVesselId ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!selectedVesselId,
    select: certs => {
      const journeyDate = dayjs(`${journey.day}T${journey.time}`);
      return certs.filter(cert => {
        const start = dayjs(cert.startDate);
        const end = dayjs(cert.endDate);
        return (
          cert.status === VesselCertificateStatus.Approved &&
          !journeyDate.isBefore(start, 'day') &&
          !journeyDate.isAfter(end, 'day')
        );
      });
    },
  });
  return (
    <Alert
      className="mb-2 bg-gray-100 text-gray-800"
      icon={<RiShip2Line className="text-black w-5 h-5" />}
    >
      {vesselCertificates.length > 0 ? (
        <>
          <AlertTitle>{vesselName}</AlertTitle>
          <ul className="list-disc pl-6 space-y-1 text-sm text-black">
            {vesselCertificates.map((cert, idx) => (
              <li key={cert.id}>
                {t('content.certification')} {idx + 1}:{' '}
                <span className="font-bold">{t('content.minimum-crew')}: </span>{' '}
                {cert.minimumCrew}
                {', '}
                <span className="font-bold">
                  {t('content.maximum-passengers')}:{' '}
                </span>{' '}
                {cert.maximumPassengers}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <AlertTitle>
          {t('journey-log-validation.vessel-certificate-not-found-for-date', {
            DATE: dayjs(journey.day).format(DAYJS_VISUALIZATION_DATE_FORMAT),
          })}
        </AlertTitle>
      )}
    </Alert>
  );
};

export default VesselCertificatesAlert;
